<template>
  <div class="event-card mb-3" :class="{ 'event-card--premium': event.is_premium }" style="cursor: pointer;" @click="emit('cardClick', event)">
    <!-- Badge de status -->
    <span :class="['event-status-tag', `status-${event.status}`]">
      {{ getStatusLabel(event.status) }}
    </span>

    <div class="event-card-inner">
      <!-- Thumbnail ou placeholder -->
      <div class="event-thumbnail">
        <img v-if="event.image" :src="event.image" :alt="event.name" />
        <div v-else class="event-thumbnail-placeholder">
          <i class="fas fa-gift"></i>
        </div>
      </div>

      <!-- Corpo do card -->
      <div class="event-body">
        <!-- Nome + badge premium -->
        <div class="event-header">
          <h5 class="event-title">
            <span v-if="event.is_premium" class="event-premium-badge" title="Evento Premium">
              <i class="fas fa-crown"></i>
            </span>
            {{ event.name }}
          </h5>
        </div>

        <!-- Data (se disponível) -->
        <p v-if="eventDate" class="event-meta">
          <i class="far fa-calendar-alt me-1"></i>{{ eventDate }}
        </p>

        <!-- Arrecadado -->
        <div class="event-collected-box">
          <div class="event-stats-inline">
            <span class="event-meta event-collected">
              <i class="fas fa-coins me-1"></i>
              <strong>{{ formatCurrency(event.details?.finalCollected || 0) }}</strong>
            </span>
            <span class="event-participants">
              <i class="fas fa-users me-1"></i>
              {{ event.details?.finalParticipants || 0 }} {{ (event.details?.finalParticipants || 0) === 1 ? 'participante' : 'participantes' }}
            </span>
          </div>
        </div>


      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount } from 'vue';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['cardClick', 'reactivate']);

const getStatusLabel = (status) => {
  const labels = {
    'in-progress': 'Ativo',
    'finished': 'Encerrado',
    'paused': 'Pausado',
    'processing': 'Processando',
    'completed': 'Concluído',
    'draft': 'Rascunho',
  };
  return labels[status] ?? 'Desconhecido';
};

const eventDate = computed(() => {
  const raw = props.event.event_date || props.event.date;
  if (!raw) return null;
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(raw));
  } catch {
    return null;
  }
});

onBeforeUnmount(() => {
  // sem instâncias Bootstrap a destruir
});
</script>

<style scoped>
.event-card {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 0.875rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.event-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

/* Layout interno: imagem + corpo */
.event-card-inner {
  display: flex;
  align-items: center;
  min-height: 120px;
}

/* Thumbnail */
.event-thumbnail {
  width: 96px;
  height: 120px;
  flex-shrink: 0;
  overflow: hidden;
  border-top-left-radius: 0.875rem;
  border-bottom-left-radius: 0.875rem;
}

.event-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.event-thumbnail-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(145deg, #ff50a3, #ff8cc8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.event-thumbnail-placeholder i {
  font-size: 1.8rem;
  color: rgba(255, 255, 255, 0.85);
}

/* Corpo */
.event-body {
  flex: 1;
  padding: 0.6rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  justify-content: center;
}

/* Cabeçalho: nome + share */
.event-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.event-title {
  font-size: 1rem;
  font-weight: 700;
  color: #32355d;
  line-height: 1.3;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Meta info */
.event-meta {
  font-size: 0.8rem;
  color: #9ea4ab;
  margin: 0;
  display: flex;
  align-items: center;
}

.event-meta i {
  color: #c0c5cc;
}

.event-collected-box {
  background: #f8f9fa;
  border: 1px solid #eceff2;
  border-radius: 0.6rem;
  padding: 0.26rem 0.45rem;
}

.event-stats-inline {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
}

.event-collected {
  font-size: 0.78rem;
  color: #32355d;
}

.event-collected i {
  color: #ff50a3;
}

.event-collected strong {
  color: #32355d;
}

.event-participants {
  font-size: 0.7rem;
  margin: 0;
  color: #6c757d;
  display: flex;
  align-items: center;
}

.event-participants i {
  color: #9ea4ab;
}

.event-card--premium {
  border-left: 3px solid #f59e0b;
}

.event-card--premium .event-thumbnail-placeholder {
  background: linear-gradient(145deg, #f59e0b, #fbbf24);
}

.event-premium-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 0.58rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  margin-right: 0.35rem;
  flex-shrink: 0;
  vertical-align: middle;
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.45);
}

/* Badge de status */
.event-status-tag {
  position: absolute;
  top: 0.55rem;
  right: 0.6rem;
  z-index: 10;
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 0.3rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.status-in-progress {
  background-color: #fff0d6;
  color: #b45309;
}

.status-finished {
  background-color: #d1fae5;
  color: #065f46;
}

.status-processing {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.status-completed {
  background-color: #ede9fe;
  color: #6d28d9;
}

.status-paused {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-draft {
  background-color: #fef9c3;
  color: #854d0e;
}

/* Mobile */
@media (max-width: 575.98px) {
  .event-card-inner {
    min-height: 108px;
  }

  .event-thumbnail {
    width: 80px;
    height: 108px;
  }

  .event-thumbnail-placeholder {
    min-height: 0;
  }

  .event-thumbnail-placeholder i {
    font-size: 1.5rem;
  }

  .event-title {
    font-size: 0.95rem;
  }

  .event-body {
    padding: 0.65rem 0.75rem;
  }

}
</style>